import Icon from 'components/atoms/Icon';
import { useGetCompaniesOptions } from 'components/molecules/SelectFormField/CompanySelectFormFieldPaginated/CompanySelectFormFieldPaginated';
import { ModalTypes } from 'enums/modalTypes';
import {
  SoftSkillCategory,
  SoftSkillFragment,
  SoftSkillGranularity,
  SoftSkillSortField,
  SortOption,
  useSoftSkillFindManyPaginatedQuery,
} from 'generated/graphql';
import { openModal } from 'graphql/modals';
import { useQueryParams } from 'hooks/useQueryParams';
import isBoolean from 'lodash/isBoolean';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { SoftSkillsFilterModal } from 'views/StaticContent/SoftSkills/components/FiltersModal/FiltersModal';
import { AddSoftSkillDrawer } from 'views/StaticContent/SoftSkills/drawers/AddSoftSkillDrawer';
import { EditSoftSkillDrawer } from 'views/StaticContent/SoftSkills/drawers/EditSoftSkillDrawer';
import { EmptyTableContent } from 'views/StaticContent/SoftSkills/EmptyTableContent';
import {
  COLUMNS,
  DEFAULT_TABLE_PAGE_SIZE,
  getTableSkillSortField,
  parseRow,
  SoftSkillDrawerTab,
  SoftSkillTableColumns,
} from 'views/StaticContent/SoftSkills/SoftSkills.helpers';
import styles from 'views/StaticContent/SoftSkills/SoftSkills.module.scss';
import { GridSortModel, ItemsCount, Table } from '@spotted-zebra-uk/ui';
import {
  Button,
  DropdownMenu,
  Search,
  Select,
  Switch,
  TNotification,
  useDrawer,
  useNotification,
} from '@spotted-zebra-uk/ui-components';

const SoftSkills: FC = () => {
  const { handleMsgType } = useNotification();
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);

  const [params, setParams] = useQueryParams([
    'search',
    'page',
    'pageSize',
    'displayArchived',
    'companyId',
    'softSkill',
    'addSoftSkill',
    'editTab',
    'orderField',
    'orderDirection',
    'categoryFilter',
    'granularityFilter',
    'taxonomyFilter',
  ]);

  const searchValue = params.search ?? '';
  const page = params.page ? Number(params.page) : 1;
  const pageSize = params.pageSize
    ? Number(params.pageSize)
    : DEFAULT_TABLE_PAGE_SIZE;
  const companyId = params.companyId ? Number(params.companyId) : null;
  const withDeleted = params.displayArchived === 'true';
  const drawerSoftSkillId = params.softSkill;
  const isAddMode = params.addSoftSkill === 'true';
  const editTab =
    (params.editTab as SoftSkillDrawerTab) ?? SoftSkillDrawerTab.TRAITS;
  const orderField =
    (params.orderField as SoftSkillSortField) || SoftSkillSortField.Name;
  const orderDirection =
    params.orderDirection === SortOption.Desc
      ? SortOption.Desc
      : SortOption.Asc;
  const categoryFilter = params.categoryFilter as SoftSkillCategory | null;
  const granularityFilter =
    params.granularityFilter as SoftSkillGranularity | null;
  const taxonomyFilter = params.taxonomyFilter;
  const rowCountRef = useRef(0);

  const setCompanyId = (companyId: number | null | undefined): void => {
    setParams({ page: null, companyId: companyId ? String(companyId) : null });
  };

  const setWithDeleted = (withDeleted: boolean): void => {
    setParams({ page: null, displayArchived: withDeleted ? 'true' : null });
  };

  const setEditTab = (tab: SoftSkillDrawerTab): void => {
    setParams({ editTab: tab });
  };

  const setIsAddMode = (isOpen: boolean): void => {
    setParams({ addSoftSkill: isOpen ? 'true' : null });
  };

  // Pagination
  const handlePaginationChange = ({
    page: newPage,
    pageSize: newPageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    const parsedNewPage = newPage + 1;

    if (page !== parsedNewPage || pageSize !== newPageSize) {
      setParams({
        page: parsedNewPage.toString(),
        pageSize: newPageSize.toString(),
      });
    }
  };

  // Ordering
  const handleSortChange = useCallback(
    (sortModel: GridSortModel) => {
      if (!sortModel[0]) return;

      const { field, sort } = sortModel[0];
      const newOrderField = getTableSkillSortField(
        field as SoftSkillTableColumns
      );

      if (!newOrderField) {
        return setParams({ orderDirection: null, orderField: null });
      }

      if (sort === 'asc') {
        return setParams({
          orderDirection: SortOption.Asc,
          orderField: newOrderField,
        });
      }

      return setParams({
        orderDirection: SortOption.Desc,
        orderField: newOrderField,
      });
    },
    [setParams]
  );

  const setSearchValue = (searchValue: string) => {
    setParams({ search: searchValue || null, page: null });
  };

  const handleSearchClearBtnClick = () => {
    setParams({ search: null, page: '1' });
  };

  const setDrawerSoftSkillId = (softSkillSubId: string | null) => {
    if (softSkillSubId) {
      setParams({
        softSkill: softSkillSubId,
        editTab: SoftSkillDrawerTab.TRAITS,
        addSoftSkill: null,
      });
    } else {
      setParams({ editTab: null, softSkill: null });
    }
  };

  const { closeDrawer } = useDrawer();

  const { data, loading } = useSoftSkillFindManyPaginatedQuery({
    variables: {
      pagination: {
        page: page,
        size: pageSize,
      },
      filters: {
        companyId: companyId || undefined,
        withDeleted: isBoolean(withDeleted) ? withDeleted : undefined,
        name: searchValue || undefined,
        category: categoryFilter || undefined,
        granularity: granularityFilter || undefined,
        taxonomyVersion: taxonomyFilter ? Number(taxonomyFilter) : undefined,
      },
      order: {
        field: orderField,
        direction: orderDirection,
      },
    },
    fetchPolicy: 'cache-and-network',
    onError: error => {
      handleMsgType({ type: TNotification.error, message: error?.message });
    },
  });

  const setDrawerSoftSkillIdCallback = useCallback(setDrawerSoftSkillId, [
    setParams,
  ]);

  const rows = useMemo(() => {
    const handleEditSoftSkillsDrawer = (softSkill: SoftSkillFragment) => {
      setDrawerSoftSkillIdCallback(softSkill.subId);
    };

    return data?.SoftSkillFindManyPaginated.data?.map(softSkill =>
      parseRow(softSkill, handleEditSoftSkillsDrawer)
    );
  }, [data?.SoftSkillFindManyPaginated.data, setDrawerSoftSkillIdCallback]);

  const rowCount = useMemo(() => {
    const itemsTotal = data?.SoftSkillFindManyPaginated?.pageInfo?.itemsTotal;
    if (itemsTotal !== undefined) {
      rowCountRef.current = itemsTotal;
    }
    return rowCountRef.current;
  }, [data?.SoftSkillFindManyPaginated?.pageInfo?.itemsTotal]);

  const getFiltersCount = () => {
    let count = 0;

    if (granularityFilter) count += 1;
    if (taxonomyFilter) count += 1;
    if (categoryFilter) count += 1;

    return count;
  };

  const companyOptionsHelpers = useGetCompaniesOptions({
    hasGlobal: true,
    initialValue: companyId ? companyId : undefined,
  });

  const selectedCompanyOption =
    companyId !== null
      ? companyOptionsHelpers.options.find(option => option.value === companyId)
      : {
        label: '',
        value: undefined,
      };

  const openUploadSoftSkillsContentModal = () => {
    openModal(ModalTypes.UPLOAD_SOFT_SKILLS_CONTENT_CSV_MODAL, {});
  };

  const openDownloadSoftSkillsContentModal = () => {
    openModal(ModalTypes.DOWNLOAD_SOFT_SKILLS_CONTENT_CSV_MODAL, {});
  };

  const actionsOptions = [
    {
      label: 'Upload soft skills content',
      onClick: openUploadSoftSkillsContentModal,
      ariaLabel: 'Upload soft skills content',
    },
    {
      label: 'Download soft skills content',
      onClick: openDownloadSoftSkillsContentModal,
      ariaLabel: 'Download soft skills content',
    }
  ];

  return (
    <div
      className={styles.container}
      data-testid="soft-skills__table-container"
    >
      <Table
        className={styles.table}
        rows={rows}
        columns={COLUMNS}
        autosizeOnMount
        loading={loading}
        rowCount={rowCount}
        paginationMode="server"
        paginationModel={{ page: page - 1, pageSize }}
        onPaginationModelChange={handlePaginationChange}
        keepNonExistentRowsSelected
        emptyTableContent={<EmptyTableContent />}
        disableColumnFilter
        onSortModelChange={handleSortChange}
        hasToolbar
        slotProps={{
          toolbar: {
            leftMainAction: <ItemsCount count={rowCount} text="Items" />,
            left: (
              <div className={styles.toolbarLeft}>
                <Select
                  id="company-selector"
                  options={companyOptionsHelpers.options}
                  onChange={({ value }) => setCompanyId(value || null)}
                  name="Owner"
                  className={styles.toolbarCompanySelector}
                  isLoading={companyOptionsHelpers.loading}
                  onClearBtnClick={() => setCompanyId(null)}
                  onMenuScrollToBottom={companyOptionsHelpers.loadMore}
                  onInputChange={companyOptionsHelpers.debouncedSearch}
                  loadingPaginatedOptions={
                    companyOptionsHelpers.paginationLoading
                  }
                  placeholder="Select owner"
                  disableFilter
                  value={selectedCompanyOption}
                />
              </div>
            ),
            right: (
              <div className={styles.toolbarRight}>
                <Switch
                  id="display-archived-toggle"
                  label="Display archived"
                  onToggle={setWithDeleted}
                  value={withDeleted}
                  size="large"
                />
                <Search
                  internalSearch={false}
                  onSearch={setSearchValue}
                  inputValue={searchValue}
                  onClearBtnClick={handleSearchClearBtnClick}
                  value={{
                    value: '',
                    label: searchValue,
                  }}
                  className={styles.toolbarSearch}
                  isDisabled={false}
                  ariaLabel="Search"
                />
                <Button
                  variant="secondary"
                  leftIcon={<Icon icon="filter" />}
                  onClick={() => setIsFiltersModalOpen(true)}
                  data-testid="technical-skills-list__filter-button"
                  counter={getFiltersCount() || undefined}
                  size="small"
                >
                  Filter
                </Button>
                <DropdownMenu
                  options={actionsOptions}
                  label="Actions"
                  ariaLabel="Soft skills actions"
                  className={styles.actionsDropdown}
                  classNameDropdownMenu={styles.dropdownMenuContainer}
                />
              </div>
            ),
            rightMainAction: (
              <Button
                className={styles.toolbarAddButton}
                onClick={() => setIsAddMode(true)}
                variant="primary"
                size="medium"
                leftIcon={<Icon icon="add" className={styles.plusIcon} />}
              >
                Add skill
              </Button>
            ),
          },
        }}
        sx={{
          minHeight: '600px',
        }}
      />

      <EditSoftSkillDrawer
        onClose={() => {
          closeDrawer();
          setDrawerSoftSkillId(null);
        }}
        softSkillSubId={drawerSoftSkillId}
        isOpen={Boolean(drawerSoftSkillId)}
        editTab={editTab}
        setEditTab={setEditTab}
      />
      <AddSoftSkillDrawer
        isOpen={isAddMode && !drawerSoftSkillId}
        onClose={() => setIsAddMode(false)}
        setIsAddMode={setIsAddMode}
        setDrawerSoftSkillId={setDrawerSoftSkillId}
      />
      {isFiltersModalOpen && (
        <SoftSkillsFilterModal
          filters={{ categoryFilter, granularityFilter, taxonomyFilter }}
          onClose={() => setIsFiltersModalOpen(false)}
          onSubmit={setParams}
        />
      )}
    </div>
  );
};

export default SoftSkills;
